import * as React from "react";
import { autorun } from "mobx";
import { useLocalization } from "@shared-ui/localization-context";

import { UitkFavoriteSwitch } from "uitk-react-switch";
import { Level, liveAnnounce } from "uitk-react-utils";

import { HeartButtonProps } from "./typings";
import { FlexClickTracker } from "components/utility/analytics/FlexClickTracker/FlexClickTracker";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { withStores } from "stores";

const TrackedFavoriteSwitch = FlexClickTracker(UitkFavoriteSwitch);

enum RfrrId {
  SAVE = "save",
  UNSAVE = "unsave",
}

export enum EntityType {
  HOTEL = "hotel",
  THING_TO_DO = "local-expert",
  POINT_OF_INTEREST = "poi-gaia",
  CITY = "destination",
  NEIGHBORHOOD = "neighborhood",
}

export const HeartButton = withStores(
  "shortlist",
  "uiPage"
)((props: HeartButtonProps) => {
  const { moduleName, heartableEntity, shortlist, uiPage } = props;
  const { id: heartId, name: heartName, entityType: heartEntityType } = heartableEntity;

  if (!(moduleName && heartId)) {
    return null;
  }

  const [isSaved, setIsSaved] = React.useState(false);

  React.useEffect(() => {
    autorun(() => {
      setIsSaved(shortlist.savedFavorites?.some((id) => id === heartId));
    });
  }, []);

  const keyHelper = new ItemKeyHelper("HeartButton");
  const { formatText } = useLocalization();

  const label = isSaved
    ? formatText("hotels.shortlist.removeFromList.a11yLabel", heartName)
    : formatText("hotels.shortlist.saveToList.a11yLabel", heartName);

  const handleClick = () => {
    if (!(heartName && heartEntityType)) {
      return;
    }

    if (isSaved) {
      shortlist.unsaveFavorite(heartableEntity);

      liveAnnounce(formatText("hotels.shortlist.removeFromList.notification", heartName), Level.POLITE);
    } else {
      const notificationText = formatText("hotels.shortlist.saveToList.notification", heartName);
      liveAnnounce(notificationText, Level.POLITE);

      shortlist.saveFavorite(heartableEntity);
      uiPage.showNotification(notificationText, "favorite");
    }

    setIsSaved(!isSaved);
  };

  return (
    <TrackedFavoriteSwitch
      id={`shortlist-item-${heartId}-${keyHelper.next()}`}
      moduleName={moduleName}
      checked={isSaved}
      onChange={handleClick}
      rfrr={`shortlist.${isSaved ? RfrrId.UNSAVE : RfrrId.SAVE}`}
      label={label}
    />
  );
});
